import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, setToken } from "@/lib/api";
import {
  BroadcastIcon,
  ChatIcon,
  ClockIcon,
  CloseIcon,
  ClusterIcon,
  DashboardIcon,
  GiftIcon,
  GlobeIcon,
  HistoryIcon,
  LayersIcon,
  LogoutIcon,
  MenuIcon,
  ServerIcon,
  SettingsIcon,
  SparkIcon,
  TicketIcon,
  UserPlusIcon,
  UsersIcon,
} from "./icons";

type NavItem = {
  to: string;
  label: string;
  end?: boolean;
  Icon: (p: { className?: string }) => JSX.Element;
};

const navGroups: { title: string | null; items: NavItem[] }[] = [
  { title: null, items: [{ to: "/", label: "Дашборд", end: true, Icon: DashboardIcon }] },
  {
    title: "Клиенты",
    items: [
      { to: "/users", label: "Пользователи", Icon: UsersIcon },
      { to: "/grants/bulk", label: "Групповая выдача", Icon: UserPlusIcon },
      { to: "/subscriptions", label: "Подписки", Icon: LayersIcon },
      { to: "/recurring", label: "Автопродление", Icon: ClockIcon },
      { to: "/pending-grants", label: "Ожидают выдачи", Icon: ClockIcon },
    ],
  },
  {
    title: "Инфраструктура",
    items: [
      { to: "/endpoints", label: "Хосты", Icon: ClusterIcon },
      { to: "/nodes", label: "Ноды", Icon: ServerIcon },
      { to: "/domains", label: "Домены", Icon: GlobeIcon },
    ],
  },
  {
    title: "Поддержка",
    items: [
      { to: "/tickets", label: "Обращения", Icon: ChatIcon },
      { to: "/operators", label: "Операторы", Icon: UsersIcon },
    ],
  },
  {
    title: "Маркетинг",
    items: [
      { to: "/broadcast", label: "Рассылка", Icon: BroadcastIcon },
      { to: "/promo-codes", label: "Промокоды", Icon: TicketIcon },
      { to: "/referrals", label: "Рефералы", Icon: GiftIcon },
    ],
  },
  {
    title: "Старая модель",
    items: [
      { to: "/servers", label: "Серверы", Icon: ServerIcon },
      { to: "/clusters", label: "Кластеры", Icon: ClusterIcon },
      { to: "/broadcasts", label: "История рассылок", Icon: HistoryIcon },
    ],
  },
  {
    title: null,
    items: [
      { to: "/bot-content", label: "Контент бота", Icon: ChatIcon },
      { to: "/funnel", label: "Автосообщения", Icon: SparkIcon },
      { to: "/settings", label: "Настройки", Icon: SettingsIcon },
    ],
  },
];

function Wordmark({ small = false }: { small?: boolean }) {
  return (
    <div className={`font-extrabold tracking-tight ${small ? "text-base" : "text-xl"}`}>
      <span className="text-grad">No</span>
      <span className="text-zinc-50">Lags</span>
    </div>
  );
}

function ChevronsIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`transition-transform ${collapsed ? "rotate-180" : ""}`}
    >
      <polyline points="11 17 6 12 11 7" />
      <polyline points="18 17 13 12 18 7" />
    </svg>
  );
}

/** Оператору поддержки в панели доступен только раздел обращений (ТЗ-03 §2). */
const OPERATOR_PATHS = new Set(["/tickets"]);

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const me = useQuery<{ telegram_id: number; role: string }>({
    queryKey: ["admin-me"],
    queryFn: () => api("/api/admin/me"),
    staleTime: 300000,
  });
  const isOperator = me.data?.role === "operator";
  const groups = isOperator
    ? navGroups
        .map((g) => ({ ...g, items: g.items.filter((i) => OPERATOR_PATHS.has(i.to)) }))
        .filter((g) => g.items.length > 0)
    : navGroups;
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("nolags_sidebar_collapsed") === "1",
  );

  useEffect(() => {
    localStorage.setItem("nolags_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", h);
      document.body.style.overflow = "";
    };
  }, [open]);

  const logout = () => {
    setToken(null);
    navigate("/login", { replace: true });
  };

  // `collapsed` только для десктопа (md+). На мобиле — выезжающий drawer w-64.
  const c = collapsed;

  return (
    <div className="h-screen flex overflow-hidden text-zinc-100">
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        className={`fixed md:static inset-y-0 left-0 z-40 shrink-0 glass md:bg-transparent md:backdrop-blur-none border-r border-white/[0.06] flex flex-col w-64 ${
          c ? "md:w-[74px]" : "md:w-64"
        } transition-[width,transform] duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
      >
        <div className={`px-5 py-5 md:py-6 flex items-center justify-between ${c ? "md:px-0 md:justify-center" : ""}`}>
          <div className={`flex items-center gap-2.5 ${c ? "md:gap-0" : ""}`}>
            <span className="w-8 h-8 rounded-xl bg-brand-grad shadow-glow flex items-center justify-center text-brand-fg font-black text-sm shrink-0">
              N
            </span>
            <div className={c ? "md:hidden" : ""}>
              <Wordmark />
              <div className="text-zinc-500 text-[10px] uppercase tracking-[0.18em] mt-0.5">
                admin panel
              </div>
            </div>
          </div>
          <button
            className="md:hidden text-zinc-400 hover:text-zinc-100"
            onClick={() => setOpen(false)}
            aria-label="Закрыть меню"
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="flex-1 px-3 space-y-4 overflow-y-auto overflow-x-hidden pb-4">
          {groups.map((group, gi) => (
            <div key={gi} className="space-y-1">
              {group.title && (
                <div
                  className={`px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-600 ${
                    c ? "md:hidden" : ""
                  }`}
                >
                  {group.title}
                </div>
              )}
              {c && group.title && (
                <div className="hidden md:block mx-2 my-1 border-t border-white/[0.05]" />
              )}
              {group.items.map(({ to, label, end, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  title={c ? label : undefined}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-xl text-sm transition-all duration-150 ${
                      c ? "md:w-11 md:h-11 md:mx-auto md:px-0 md:justify-center px-3 py-2" : "px-3 py-2"
                    } ${
                      isActive
                        ? "bg-brand-soft text-brand font-semibold ring-1 ring-inset ring-brand/25"
                        : "text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-100"
                    }`
                  }
                >
                  <Icon className="shrink-0" />
                  <span className={`truncate ${c ? "md:hidden" : ""}`}>{label}</span>
                </NavLink>
              ))}
            </div>
          ))}

          <button
            onClick={logout}
            title={c ? "Выйти" : undefined}
            className={`mt-4 w-full flex items-center gap-3 rounded-xl text-sm text-zinc-500 hover:bg-red-500/10 hover:text-red-400 transition-colors ${
              c ? "md:justify-center md:px-0 md:h-10 px-3 py-2" : "px-3 py-2"
            }`}
          >
            <LogoutIcon className="shrink-0" />
            <span className={c ? "md:hidden" : ""}>Выйти</span>
          </button>
        </nav>

        {/* collapse toggle — только десктоп */}
        <button
          onClick={() => setCollapsed((v) => !v)}
          className={`hidden md:flex items-center gap-2 px-4 py-3 border-t border-white/[0.06] text-zinc-500 hover:text-zinc-200 transition-colors ${
            c ? "justify-center px-0" : ""
          }`}
          aria-label={c ? "Развернуть меню" : "Свернуть меню"}
        >
          <ChevronsIcon collapsed={c} />
          <span className={`text-xs ${c ? "hidden" : ""}`}>Свернуть</span>
        </button>
      </aside>

      <main className="flex-1 min-w-0 overflow-auto">
        <div className="md:hidden sticky top-0 z-20 flex items-center gap-3 px-4 py-3 glass border-b border-white/[0.06]">
          <button
            className="text-zinc-300 hover:text-zinc-100 w-9 h-9 flex items-center justify-center -ml-2"
            onClick={() => setOpen(true)}
            aria-label="Открыть меню"
          >
            <MenuIcon width={22} height={22} />
          </button>
          <Wordmark small />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
